import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Chart } from 'primereact/chart';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { actionStyles, GameplanProps, getActionColor, gamePlanColors } from '../../gamePlan/gamePlanType';
import { metricValueFormat } from '../../../utils/metricValueFormat';

import VectorOne from '../../../assets/gamePlan/vector-1.svg';
import OpenLink from '../../../assets/OpenLink.png';
import gameplanNodata from '../../../assets/gameplan_nodata.png';
import { GAMEPLAN } from '../../../constants';
import './gameplanLandingPage.scss';

export const GameplanLandingPage = ({ gamePlan }: GameplanProps) => {
    const navigate = useNavigate();
    const [gamePlanData, setGamePlanData] = useState<any>();
    const [chartData, setChartData] = useState<any>({});
    const [chartOptions, setChartOptions] = useState<any>({});
    const [teamStats, setTeamStats] = useState<any[]>([]);

    useEffect(() => {
        setGamePlanData(gamePlan);
    }, [gamePlan]);

    useEffect(() => {
        if (gamePlanData?.data?.length > 0) {
            const countByActionId = gamePlanData.data.reduce((acc: any, item: any) => {
                const actionId = item.gameplan_action_id;
                if (typeof actionId === 'number') {
                    return { ...acc, [actionId]: (acc[actionId] || 0) + 1 };
                }
                return acc;
            }, {});

            const actionMapping: Record<string, { label: string; color: string }> = {
                'No Data Available': { label: 'No Data', color: gamePlanColors.noData },
                Coach: { label: 'Needs Coaching', color: gamePlanColors.coach },
                Recognize: { label: 'Deserves Recognition', color: gamePlanColors.recognize },
            };

            const actionDetails: Record<number, { label: string; color: string }> = {};
            gamePlanData.data.forEach((item: any) => {
                const id = item.gameplan_action_id;
                const name = item.gameplan_action_name;
                if (!actionDetails[id] && actionMapping[name]) {
                    actionDetails[id] = actionMapping[name];
                }
            });

            const totalCount = Number(gamePlanData.data.length) || 0;
            const stats = Object.entries(countByActionId).map(([actionIdStr, count]) => {
                const actionId = parseInt(actionIdStr, 10);
                const safeCount = Number(count) || 0;
                const details = actionDetails[actionId];
                const percentage = totalCount > 0
                    ? Number(((safeCount / totalCount) * 100).toFixed(2))
                    : 0;
                return {
                    percentage,
                    label: details?.label || 'Unknown',
                    color: details?.color || '#000',
                };
            });
            setTeamStats(stats);

            const backgroundColors = Object.keys(countByActionId).map((actionIdStr) => {
                const actionId = parseInt(actionIdStr, 10);
                return actionDetails[actionId]?.color || '#000';
            });

            setChartData({
                datasets: [{
                    data: Object.values(countByActionId),
                    label: 'Recommendation',
                    backgroundColor: backgroundColors,
                    hoverBackgroundColor: backgroundColors,
                }],
            });

            setChartOptions({ cutout: '60%' });
        } else {
            setChartData([]);
            setTeamStats([]);
        }
    }, [gamePlanData]);

    // ── Templates ─────────────────────────────────────────────────────────────
    const recommendationBodyTemplate = (rowData: any) => {
        const action = actionStyles[rowData.gameplan_action_id];
        if (!action) return null;

        return (
            <div style={{ position: 'relative', height: '30px' }}>
                <img
                    src={VectorOne}
                    alt="Vector"
                    style={{ position: 'absolute', width: '14px', height: '13px', top: '17px', left: 0 }}
                />
                <Button
                    iconPos="left"
                    style={{
                        backgroundColor: action.color,
                        height: '29px',
                        padding: '4px 8px',
                        borderRadius: '8px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '130px',
                        cursor: 'default',
                        fontSize: '13px',
                    }}
                >
                    <img src={action.icon} alt={rowData.gameplan_action_name} style={{ width: '18px', height: '15px' }} />
                    <span>{rowData.gameplan_action_id === 1 ? 'No Data' : rowData.gameplan_action_name}</span>
                </Button>
            </div>
        );
    };

    const metricTemplate = (rowData: any) => (
        <span>{rowData.metric_value === null ? '--' : rowData.label}</span>
    );

    const metricValueTemplate = (rowData: any) => (
        <span style={{ color: getActionColor(rowData.gameplan_action_id) }}>
            {metricValueFormat(rowData.metric_value, rowData.metric_type_name)}
        </span>
    );

    const handleOpenGameplan = () => {
        navigate(`${GAMEPLAN}`, { replace: true });
    };

    const supervisorName = gamePlanData?.data?.[0]?.supervisor_name ?? 'there';
    const hasData = gamePlanData?.data?.length > 0;

    return (
        <div className="gp-landing">
            {/* ── Header ── */}
            <div className="gp-landing__header">
                You can see all information in the
                <Button
                    className="p-button gp-landing__header-btn"
                    onClick={handleOpenGameplan}
                >
                    <img src={OpenLink} alt="icon" style={{ width: '16px', height: '16px' }} />
                    <span>Gameplan</span>
                </Button>
                section.
            </div>

            {/* ── Body ── */}
            <div className="gp-landing__body">

                {/* Left: Table */}
                <div className="gp-landing__table-wrapper">
                    {!hasData ? (
                        <div className="gp-landing__no-data">
                            <img src={gameplanNodata} alt="No Data" style={{ maxWidth: '70%' }} />
                        </div>
                    ) : (
                        <DataTable
                            value={gamePlanData?.data}
                            emptyMessage="No records found."
                            className="gameplan"
                            scrollable
                            scrollHeight="360px"
                            style={{ fontSize: '13px' }}
                        >
                            <Column header="Associate" field="employee_name" sortable />
                            <Column header="Recommendation" sortField="gameplan_action_name" body={recommendationBodyTemplate} sortable />
                            <Column header="Metric" body={metricTemplate} sortField="label" sortable />
                            <Column header="Value" body={metricValueTemplate} />
                        </DataTable>
                    )}
                </div>

                {/* Right: Team Stats */}
                <div className="gp-landing__stats">
                    {hasData && <h3 className="gp-landing__stats-title">Team Stats</h3>}

                    <div className="gp-landing__chart-row">
                        <div style={{ position: 'relative', width: '202px', height: '202px' }}>
                            <Chart
                                type="doughnut"
                                data={chartData}
                                options={chartOptions}
                                className="w-full md:w-30rem"
                            />
                        </div>

                        {/* Legend */}
                        <div className="gp-landing__legend">
                            {hasData && teamStats.map((stat: any, index: number) => (
                                <div key={index} className="gp-landing__legend-item">
                                    <span
                                        className="gp-landing__legend-dot"
                                        style={{ backgroundColor: stat.color }}
                                    />
                                    <p className="gp-landing__legend-text">
                                        <span style={{ fontWeight: 'bold' }}>{stat.percentage}%</span>
                                        <span> - {stat.label}</span>
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Message */}
                    {hasData && (
                        <p className="gp-landing__stats-message">
                            Hello {supervisorName}, review associates who{' '}
                            <span style={{ color: gamePlanColors.coach, fontWeight: 600 }}>Needs Coaching</span>{' '}
                            and who{' '}
                            <span style={{ color: gamePlanColors.recognize, fontWeight: 600 }}>Deserves Recognition</span>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};